
import { defineComponent, computed, onMounted, ref } from "vue";
import CommonRecipeList from "@/components/commonRecipe/CommonRecipeList.vue";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import store from "@/store";
import router from "@/router";
import {
  RequestRecommendationRecipe,
  RequestFoodsRecommendationList,
  FoodRecommendationListData,
  FoodRecommendationList,
} from "@/types/home";
import NativeClient, { TitleInfo } from "@/client/native-client";
import CommonUtil from "@/utils/common-utils";
import { BeaconLogInfo } from "@/types/common";
import { PAGE_ID } from "@/const/log-const";
import { CommonRecipe } from "@/types/commonRecipe";
import {
  createRecipeToCommonRecipe,
} from "@/utils/search-util";
import {
  getFoodIds,
  getRecommendFoodList
} from "@/utils/home-util";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
export default defineComponent({
  name: "HomeRecommendPage",
  components: { CommonRecipeList, CommonDesignDialog },
  setup() {
    const ERROR_BUTTON_LABEL = "OK";
    const limitCount = -1;
    const nativeToken = ref("");
    const recommendRecipes = ref(Array<CommonRecipe>());
    const foodRecommendationData = ref(new FoodRecommendationListData(null));
    NativeClient.setTitle(new TitleInfo(false, ""));
    CommonUtil.scroll({ top: 0, left: 0 });

    // urlから選択されたtypeを取得
    const recipeType = String(router.currentRoute.value.query.type);

    /** APIリクエスト時のエラーハンドル */
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();

    /** ダイアログの制御 */
    const { closeDialog } = useApiErrorDialog();

    const isLogin = () => {
      return nativeToken.value;
    };

    const getRecommendRecipes = () => {
      const result = store.getters["home/getRecommendRecipes"];
      NativeClient.sendDebugLog(
        "HomeRecommendPage getRecommendRecipes result=" + JSON.stringify(result)
      );
      recommendRecipes.value.length = 0;
      const commonRecipe = Array<CommonRecipe>();
      if (result != null && result.recipes != null) {
        for (let i = 0; i < result.recipes.length; i++) {
          const foodList = getFoodIds(result.recipes[i], foodRecommendationData.value);
          const recipe = createRecipeToCommonRecipe(result.recipes[i], foodList);
          commonRecipe.push(recipe);
        }
        NativeClient.sendDebugLog(
          "HomeRecommendPage getRecommendRecipes commonRecipe=" + JSON.stringify(commonRecipe)
        );
        recommendRecipes.value = commonRecipe;
      }
    };

    const setFoodRecommendation = () => {
      const result:FoodRecommendationList = store.getters["home/getFoodRecommendationList"];
      NativeClient.sendDebugLog(
        "HomeRecommendPage setFoodRecommendation result=" + JSON.stringify(result)
      );
      foodRecommendationData.value.response = result;
      NativeClient.sendDebugLog(
        "HomeRecommendPage setFoodRecommendation 2 result=" + JSON.stringify(result)
      );
    };

    /**
     * おすすめ食材を元にレシピリストを取得する
     */
    const getRecommendFoodRecipes = async () => {
      setFoodRecommendation();
      const foodList = getRecommendFoodList(foodRecommendationData.value);
      const recipePayload = new RequestRecommendationRecipe(foodList, false);
      NativeClient.sendDebugLog("HomeRecommendPage getRecipes");
      await Promise.all([
        store.dispatch("home/getRecommendRecipes", recipePayload)
      ]).then(result => {
        getRecommendRecipes();
        store.dispatch("common/stopLoading");
      });
    };

    /**
     * レシピリストを取得する
     */
    const getRecipes = async () => {
      store.dispatch("common/startLoading");
      await Promise.all([NativeClient.getApiParams(), NativeClient.getUpdateDateRecommendFoods()]).then(result => {
        const apiParams = store.getters["common/getApiBaseParams"];
        NativeClient.sendDebugLog(
          "HomeRecommendPage getRecipes apiParams = " + JSON.stringify(apiParams) + ", lastUpdateRecommendationDate = " + `${result[1]}`
        );
        nativeToken.value = apiParams.token;
        const parameter = new RequestFoodsRecommendationList();
        const latestRecommended = `${result[1]}`;
        if (latestRecommended.length > 0) {
          parameter.latestRecommended = latestRecommended;
        }
        NativeClient.sendDebugLog(
          "HomeRecommendPage getRecipes getFoodRecommendationList parameter = " + JSON.stringify(parameter)
        );
        if (isLogin()) {
          Promise.all([
            store.dispatch("home/getFoodRecommendationList", parameter),
          ]).then(result => {
            getRecommendFoodRecipes();
          });
        } else {
          Promise.all([
            store.dispatch("home/getRecommendRecipes", new RequestRecommendationRecipe(null, false)),
          ]).then(result => {
            getRecommendRecipes();
            store.dispatch("common/stopLoading");
          });
        }
      });
    };

    /**
     * レシピリストの取得を実行する
     */
    const execute = async () => {
      getRecipes();
    };

    /**
     * コンポーネントがマウントされたときに通知される
     */
    onMounted(() => {
      const beaconLogInfo = new BeaconLogInfo(PAGE_ID.HOME_RECOMMEND, null, new Array<string>());
      NativeClient.sendViewedLog(beaconLogInfo);
      execute();
    });

    /**
     * レシピ種別からタイトルを取得
     */
    const getTitle = () => {
      return "おすすめレシピ";
    };

    /**
     * 選択されたレシピをNativeに通知する
     */
    const selectRecipe = (id: number, type: string) => {
      // Native側にレシピ詳細画面に遷移指示を出す
      NativeClient.sendDebugLog(
        "HomeRecommendPage selectRecipe id=" + id + " type=" + type
      );
      NativeClient.transitionReceipDetail(id, type);
    };

    const closeError = () => {
      closeDialog();
    };

    return {
      recommendRecipes,
      limitCount,
      selectRecipe,
      recipeType,
      getTitle,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      closeError,
      ERROR_BUTTON_LABEL
    };
  }
});
